/**
 * Shared helpers for the Alpine component modules in this directory.
 *
 * Also owns the htmx:afterSwap → Alpine.initTree glue: Alpine walks the DOM
 * once at page load, so x-data on HTMX-swapped content is initialised here.
 */
export function get_cookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function set_cookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  let cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
  if (location.protocol === "https:") {
    cookie += ";Secure";
  }
  // biome-ignore lint/suspicious/noDocumentCookie: the one cookie writer, for the consent cookie; the Cookie Store API is not available in every browser we support.
  document.cookie = cookie;
}

// The one owner of the analytics-consent cookie and the PostHog opt-in/out
// calls that follow it. Audited by .github/workflows/claude-privacy-check.yml.
export const CONSENT_COOKIE = "dagonet_consent";
const CONSENT_DAYS = 365;
export const consent = {
  accept() {
    set_cookie(CONSENT_COOKIE, "accepted", CONSENT_DAYS);
    if (window.posthog) window.posthog.opt_in_capturing();
  },
  decline() {
    set_cookie(CONSENT_COOKIE, "declined", CONSENT_DAYS);
    if (window.posthog) window.posthog.opt_out_capturing();
  },
  is_accepted() {
    return get_cookie(CONSENT_COOKIE) === "accepted";
  },
  is_decided() {
    return get_cookie(CONSENT_COOKIE) !== null;
  },
};

const FOCUSABLE =
  'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"]), button';

export function describe_el(el) {
  return el.tagName.toLowerCase() + (el.id ? `#${el.id}` : "");
}

// data-* readers. A missing attribute is a legitimate "none" and yields the
// fallback silently; a present-but-malformed one is a server-side rendering
// bug, so it is logged before the fallback is returned.
export function read_json_attr(el, datasetKey, fallback) {
  const raw = el.dataset[datasetKey];
  if (raw === undefined || raw === "") return fallback;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.error(`read_json_attr: ${describe_el(el)} data-${datasetKey} is not JSON`, raw, err);
    return fallback;
  }
}

export function read_int_attr(el, datasetKey, fallback) {
  const raw = el.dataset[datasetKey];
  if (raw === undefined || raw === "") return fallback;
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) {
    console.error(`read_int_attr: ${describe_el(el)} data-${datasetKey} is not an integer`, raw);
    return fallback;
  }
  return parsed;
}

// Listener bookkeeping for components that subscribe to document / window /
// media-query events: every listen() is undone by one unlisten_all(this) in
// destroy(), so no component can forget one of its handlers.
//
// The component must declare `_listeners: []` as its own field. `this` in an
// Alpine component is a merged-scope proxy that reads and writes an
// undeclared name on the nearest *ancestor* scope that has it, so a nested
// component without the field would record its listeners on — and later
// tear down — its parent's list. test_alpine_registrations.py enforces the
// declaration.
export function listen(component, target, event, handler) {
  target.addEventListener(event, handler);
  component._listeners.push([target, event, handler]);
}

export function unlisten_all(component) {
  const entries = component._listeners;
  for (let i = 0; i < entries.length; i++) {
    entries[i][0].removeEventListener(entries[i][1], entries[i][2]);
  }
  component._listeners = [];
}

// Environment → active personas map, rendered as data-env-personas JSON on
// the persona selector and the multi-actor editor.
export function env_persona_map(rootEl) {
  const envPersonas = read_json_attr(rootEl, "envPersonas", {});
  return {
    _envPersonas: envPersonas,
    personasFor(env) {
      return envPersonas[env] || [];
    },
  };
}

// Rewrite one query parameter on an element's hx-get and re-process it so
// the next poll carries it. Run detail keeps its live poller on the visible
// view/tab this way.
export function set_poller_param(el, key, value) {
  const hxGet = el.getAttribute("hx-get");
  if (!hxGet) return;
  const url = new URL(hxGet, window.location.origin);
  url.searchParams.set(key, value);
  el.setAttribute("hx-get", url.pathname + url.search);
  htmx.process(el);
}

// Save-and-restore focus for dialogs. Spread into a component's scope; the
// component decides *what* to focus on open (focus / focusFirst) and calls
// restore() on close. Methods use this.$nextTick, so they only work spread
// into an Alpine scope.
export function focus_scope() {
  return {
    _previouslyFocused: null,
    capture() {
      this._previouslyFocused = document.activeElement;
    },
    focus(el) {
      this.$nextTick(() => {
        if (el && typeof el.focus === "function") el.focus();
      });
    },
    focusFirst(root) {
      this.$nextTick(() => {
        const first = root?.querySelector(FOCUSABLE);
        if (first) first.focus();
      });
    },
    restore() {
      const el = this._previouslyFocused;
      if (!el) return;
      this._previouslyFocused = null;
      this.$nextTick(() => {
        if (document.contains(el) && typeof el.focus === "function") el.focus();
      });
    },
  };
}

// Initialise Alpine components in every HTMX-swapped subtree. Alpine only
// walks the DOM once at page load, so x-data / x-teleport / x-for on content
// swapped in later do nothing unless we re-scan. initTree is idempotent on
// nodes that have already been initialised.
//
// initTree runs x-teleport, which clones the template's content into its
// destination (usually <body>) — outside the swap target htmx processes, so
// hx-* on the clone would stay dead and a form would fall back to a native
// GET with its fields in the URL (#5603). Alpine marks every clone with
// data-teleport-target; htmx.process skips nodes it has already processed,
// so processing them all is safe and also reaches clones nested in clones.
document.addEventListener("htmx:afterSwap", (evt) => {
  Alpine.initTree(evt.detail.target);
  for (const clone of document.querySelectorAll("[data-teleport-target]")) {
    htmx.process(clone);
  }
});
